import { useState, type ReactNode } from "react";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import { RowPanel, type RowHeadingLevel } from "@/base/wizard/RowPanel";

// Rows a broker adds and removes (BRK-S300): locations, the class codes at a
// location, a building's floors. A row's body may hold another RepeatableRows,
// so rows nest under a parent — two levels deep for a BOP-shaped application
// (locations → buildings → class codes; test/support/bopShapedWizard.tsx).
//
// Each row is a `RowPanel`, a region named by `itemLabel`, so nested rows stay
// addressable ("Building 1" inside "Location 2"); its heading sits one level
// under whatever holds it (`headingLevel`, BRK-S349). A row holding rows of
// its own asks before it goes (`removePrompt`), and `minRows` keeps the ones a
// step requires. Rows stack one per line, so they fit at 375px.

export function RepeatableRows<Row>({
  label,
  rows,
  onChange,
  newRow,
  renderRow,
  itemLabel,
  addLabel,
  minRows = 0,
  removePrompt,
  rowKey,
  headingLevel = 3,
}: {
  /** Names the group, e.g. "Locations". */
  label: string;
  rows: readonly Row[];
  onChange: (rows: Row[]) => void;
  newRow: () => Row;
  renderRow: (
    row: Row,
    update: (next: Row) => void,
    index: number,
  ) => ReactNode;
  /** Names one row, e.g. "Location 2". */
  itemLabel: (row: Row, index: number) => string;
  addLabel: string;
  minRows?: number;
  /** A question to ask before removing this row, or null to remove at once. */
  removePrompt?: (row: Row, index: number) => string | null;
  /** A stable key, when rows carry one (a saved location's id). */
  rowKey?: (row: Row, index: number) => string;
  /** Each row's heading level: h4 inside a card titled in h3. */
  headingLevel?: RowHeadingLevel;
}) {
  const [pending, setPending] = useState<{
    index: number;
    question: string;
  } | null>(null);

  const update = (index: number, next: Row) =>
    onChange(rows.map((r, i) => (i === index ? next : r)));
  const remove = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));

  function askToRemove(index: number) {
    const question = removePrompt?.(rows[index], index) ?? null;
    if (question) setPending({ index, question });
    else remove(index);
  }

  const canRemove = rows.length > minRows;

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-3">
      {rows.map((row, i) => {
        const name = itemLabel(row, i);
        return (
          <RowPanel
            key={rowKey ? rowKey(row, i) : i}
            title={name}
            headingLevel={headingLevel}
            action={
              canRemove && (
                <button
                  type="button"
                  onClick={() => askToRemove(i)}
                  aria-label={`Remove ${name}`}
                  className={`text-meta text-brk-dim underline decoration-dotted hover:text-brk-text ${TOUCH_TARGET}`}
                >
                  Remove
                </button>
              )
            }
          >
            {renderRow(row, (next) => update(i, next), i)}
          </RowPanel>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([...rows, newRow()])}
        className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
      >
        {addLabel}
      </button>
      <ConfirmDialog
        open={pending !== null}
        question={pending?.question ?? ""}
        confirmLabel="Remove"
        cancelLabel="Keep it"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) remove(pending.index);
          setPending(null);
        }}
      />
    </div>
  );
}
